import { COLLAPSIBLE_GROUPS as CP_HEADER_GROUPS } from "./CollapsibleHeaderGroup";

/** SQL/MSSQL a veces devuelve columnas en minúsculas; el UI usa PascalCase. */
export function normalizeTimelineTaskRow(t) {
  if (!t || typeof t !== "object") return t;
  const pick = (a, b) => (a !== undefined && a !== null ? a : b);
  return {
    ...t,
    Id: pick(t.Id, t.id),
    TaskName: pick(t.TaskName, t.taskname),
    ExpectedDate: pick(t.ExpectedDate, t.expecteddate),
    UpdatedDate: pick(t.UpdatedDate, t.updateddate),
    RealDate: pick(t.RealDate, t.realdate),
    IdPresupuestoA3Pedido: pick(
      t.IdPresupuestoA3Pedido,
      t.idpresupuestoa3pedido,
    ),
  };
}

export function getPedidoIdFromTask(t) {
  const v = t?.IdPresupuestoA3Pedido ?? t?.idpresupuestoa3pedido;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Solo tareas timeline antiguas (sin pedido A3). */
export function filterLegacyTimelineTasksOnly(items) {
  const list = Array.isArray(items) ? items.map(normalizeTimelineTaskRow) : [];
  return list.filter((t) => getPedidoIdFromTask(t) == null);
}

/** En filas con CP por pedido: solo timeline del circuito nuevo (por pedido A3). */
export function filterNonLegacyTimelineTasks(items, idPresupuestoA3Pedido) {
  const list = Array.isArray(items) ? items.map(normalizeTimelineTaskRow) : [];
  const idPed =
    idPresupuestoA3Pedido != null &&
    Number.isFinite(Number(idPresupuestoA3Pedido))
      ? Number(idPresupuestoA3Pedido)
      : null;
  return list.filter((t) => {
    const taskPed = getPedidoIdFromTask(t);
    if (idPed != null) return taskPed === idPed;
    return taskPed != null;
  });
}

function headerGroupFields(groupId) {
  const fields = CP_HEADER_GROUPS[groupId];
  if (!Array.isArray(fields)) return [];
  return fields.filter((f) => !String(f).startsWith("__placeholder"));
}

export function normalizeTaskKey(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

export function sameTaskName(a, b) {
  const na = normalizeTaskKey(a);
  const nb = normalizeTaskKey(b);
  return na.length > 0 && na === nb;
}

/** Orden canónico del drawer/grid (CollapsibleHeaderGroup), no createdAt del API. */
export function sortTimelineTasksByFieldOrder(tasks, orderedFieldNames) {
  if (!Array.isArray(tasks) || tasks.length <= 1) {
    return Array.isArray(tasks) ? [...tasks] : [];
  }
  const labels = Array.isArray(orderedFieldNames) ? orderedFieldNames : [];
  const orderIndex = new Map();
  labels.forEach((label, index) => {
    const key = normalizeTaskKey(label);
    if (key && !orderIndex.has(key)) orderIndex.set(key, index);
  });
  const fallback = orderIndex.size + tasks.length;
  return [...tasks].sort((a, b) => {
    const ia =
      orderIndex.get(normalizeTaskKey(a?.TaskName)) ??
      orderIndex.get(normalizeTaskKey(a?.taskname)) ??
      fallback;
    const ib =
      orderIndex.get(normalizeTaskKey(b?.TaskName)) ??
      orderIndex.get(normalizeTaskKey(b?.taskname)) ??
      fallback;
    if (ia !== ib) return ia - ib;
    const idA = Number(a?.Id);
    const idB = Number(b?.Id);
    if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) {
      return idA - idB;
    }
    return String(a?.TaskName ?? "").localeCompare(String(b?.TaskName ?? ""));
  });
}

const LOGISTIC_GRID_FIELDS = headerGroupFields("Envío y logística");
const LOGISTIC_GRID_FIELD_KEYS = new Set(
  LOGISTIC_GRID_FIELDS.map((f) => normalizeTaskKey(f)),
);
const CRITICAL_DATE_HEADER_FIELDS = headerGroupFields("Fechas críticas");

const GRID_TIMELINE_FIELD_ORDER = Object.values({
  "Samples and approvals": headerGroupFields("Muestras y aprobaciones"),
  Production: headerGroupFields("Producción"),
  "Critical dates": headerGroupFields("Fechas críticas"),
  Logistic: LOGISTIC_GRID_FIELDS,
}).flat();

/** Campos de envío/logística del grid: en el drawer CP van solo por LogisticsSection, no como tarjetas timeline. */
export function isLogisticGridTimelineTask(taskName) {
  const key = normalizeTaskKey(taskName);
  if (!key) return false;
  if (LOGISTIC_GRID_FIELD_KEYS.has(key)) return true;
  const legacyLabels = [
    "podate",
    "bookingdone",
    "documentsapproved",
    "telexdocs",
    "eta",
  ];
  return legacyLabels.includes(key);
}

function findTimelineCellInRow(row, taskName) {
  if (!row || typeof row !== "object") return null;
  const want = normalizeTaskKey(taskName);
  if (!want) return null;
  for (const k of Object.keys(row)) {
    if (normalizeTaskKey(k) === want) {
      const cell = row[k];
      if (cell && typeof cell === "object") return { rowKey: k, cell };
    }
  }
  return null;
}

function pickGridCellForTask(row, taskName) {
  return findTimelineCellInRow(row, taskName)?.cell ?? null;
}

/** Si el API devuelve vacío o sin fechas, reutilizamos las celdas ya pintadas en el grid horizontal. */
export function buildTimelineFromGridRow(
  row,
  stampPedidoId = null,
  { excludeLogisticFields = false } = {},
) {
  if (!row || typeof row !== "object") return [];
  const idPedStamp =
    stampPedidoId != null && Number.isFinite(Number(stampPedidoId))
      ? Number(stampPedidoId)
      : null;
  const out = [];
  for (const name of GRID_TIMELINE_FIELD_ORDER) {
    if (excludeLogisticFields && isLogisticGridTimelineTask(name)) continue;
    const found = findTimelineCellInRow(row, name);
    if (!found) continue;
    const { rowKey, cell } = found;
    if (
      cell.ExpectedDate == null &&
      cell.UpdatedDate == null &&
      cell.RealDate == null &&
      cell.Id == null
    ) {
      continue;
    }
    out.push({
      Id: cell.Id,
      TaskName: rowKey,
      ExpectedDate: cell.ExpectedDate ?? null,
      UpdatedDate: cell.UpdatedDate ?? null,
      RealDate: cell.RealDate ?? null,
      ...(idPedStamp != null ? { IdPresupuestoA3Pedido: idPedStamp } : {}),
    });
  }
  return out;
}

function timelineEntryScore(t) {
  return (
    (t?.ExpectedDate ? 4 : 0) +
    (t?.UpdatedDate ? 2 : 0) +
    (t?.RealDate ? 1 : 0) +
    (t?.Id != null ? 0.5 : 0)
  );
}

export function dedupeTimelineByTaskName(items) {
  if (!Array.isArray(items)) return [];
  const byKey = new Map();
  for (const t of items) {
    const key = normalizeTaskKey(t?.TaskName);
    if (!key) continue;
    const prev = byKey.get(key);
    if (!prev || timelineEntryScore(t) > timelineEntryScore(prev)) {
      byKey.set(key, t);
    }
  }
  return [...byKey.values()];
}

function filterOutLogisticTimelineTasks(
  items,
  { keepShipmentMethod = true } = {},
) {
  if (!Array.isArray(items)) return [];
  return items.filter((t) => {
    if (
      keepShipmentMethod &&
      normalizeTaskKey(t?.TaskName) === "shipmentmethod"
    ) {
      return true;
    }
    return !isLogisticGridTimelineTask(t?.TaskName);
  });
}

/** Sincroniza fechas del drawer en la fila del grid (misma forma que /critical-path-horizontal1). */
export function applyTimelineTasksToCpRow(row, timelineTasks) {
  if (!row || typeof row !== "object" || !Array.isArray(timelineTasks)) {
    return row;
  }
  const next = { ...row };
  for (const raw of timelineTasks) {
    const t = normalizeTimelineTaskRow(raw);
    const name = t?.TaskName;
    if (!name) continue;
    next[name] = {
      Id: t.Id ?? null,
      ExpectedDate: t.ExpectedDate ?? null,
      UpdatedDate: t.UpdatedDate ?? null,
      RealDate: t.RealDate ?? null,
      ExpectedUpdated: t.ExpectedUpdated ?? null,
      ExpectedReal: t.ExpectedReal ?? null,
    };
  }
  return next;
}

export function applyGridSnapshotToTimeline(
  apiData,
  gridRow,
  isMuestra,
  {
    hideLegacyTimeline = false,
    preferLegacyTimeline = false,
    idPresupuestoA3Pedido = null,
    /** Drawer CP: no mezclar columnas de logística del grid como tarjetas timeline. */
    excludeLogisticFields = false,
  } = {},
) {
  const applyTimelineFilter = (items) => {
    if (preferLegacyTimeline) {
      return filterLegacyTimelineTasksOnly(items);
    }
    if (hideLegacyTimeline) {
      return filterNonLegacyTimelineTasks(items, idPresupuestoA3Pedido);
    }
    return Array.isArray(items) ? items.map(normalizeTimelineTaskRow) : [];
  };

  /** API primero; el grid solo rellena huecos (evita revertir fechas tras guardar en el drawer). */
  const pickDatePreferApi = (apiVal, gridVal) => {
    if (
      apiVal !== undefined &&
      apiVal !== null &&
      String(apiVal).trim() !== ""
    ) {
      return apiVal;
    }
    if (
      gridVal !== undefined &&
      gridVal !== null &&
      String(gridVal).trim() !== ""
    ) {
      return gridVal;
    }
    return null;
  };

  const mergeWithGrid = (base, { lockTaskIds = false } = {}) =>
    dedupeTimelineByTaskName(
      base.map((t) => {
        const cell = pickGridCellForTask(gridRow, t.TaskName);
        if (!cell) return t;
        return {
          ...t,
          Id: lockTaskIds ? t.Id : (t.Id ?? cell.Id),
          ExpectedDate: pickDatePreferApi(t.ExpectedDate, cell.ExpectedDate),
          UpdatedDate: pickDatePreferApi(t.UpdatedDate, cell.UpdatedDate),
          RealDate: pickDatePreferApi(t.RealDate, cell.RealDate),
        };
      }),
    );

  if (isMuestra || !gridRow) {
    return dedupeTimelineByTaskName(applyTimelineFilter(apiData));
  }

  const idPed =
    idPresupuestoA3Pedido != null &&
    Number.isFinite(Number(idPresupuestoA3Pedido))
      ? Number(idPresupuestoA3Pedido)
      : null;
  const stampPedidoId = preferLegacyTimeline ? null : idPed;
  const lockTaskIds = idPed != null;

  let base = applyTimelineFilter(apiData);
  if (excludeLogisticFields) {
    base = filterOutLogisticTimelineTasks(base);
  }

  // Fila con pedido A3: solo timeline API del pedido (Ids propios; no mezclar legacy del grid).
  if (lockTaskIds) {
    if (base.length > 0) {
      const result = mergeWithGrid(base, { lockTaskIds: true });
      return excludeLogisticFields
        ? filterOutLogisticTimelineTasks(result)
        : result;
    }
    return [];
  }

  const gridTasks = applyTimelineFilter(
    buildTimelineFromGridRow(gridRow, stampPedidoId, {
      excludeLogisticFields,
    }).map(normalizeTimelineTaskRow),
  );

  if (base.length > 0) {
    const merged = dedupeTimelineByTaskName([...base, ...gridTasks]);
    const result = mergeWithGrid(merged);
    return excludeLogisticFields
      ? filterOutLogisticTimelineTasks(result)
      : result;
  }
  if (gridTasks.length > 0) {
    return dedupeTimelineByTaskName(gridTasks);
  }
  return [];
}

/** Completa DD si el API aún no trae la tarea (timelines antiguos). */
export function mergeMissingHeaderGroupTasks(timeline) {
  const list = Array.isArray(timeline)
    ? timeline.map(normalizeTimelineTaskRow)
    : [];
  const have = new Set(
    list.map((t) => normalizeTaskKey(t?.TaskName)).filter(Boolean),
  );
  const extras = [];
  for (const name of CRITICAL_DATE_HEADER_FIELDS) {
    if (normalizeTaskKey(name) !== "dd") continue;
    if (have.has("dd")) continue;
    extras.push({
      TaskName: name,
      ExpectedDate: null,
      UpdatedDate: null,
      RealDate: null,
    });
  }
  return extras.length ? [...list, ...extras] : list;
}
